
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { TopNavigation } from '../components/TopNavigation'
import { AgentSidebar } from '../components/AgentProfile/AgentSidebar'
import { agentService, type Agent } from '../services/agentService'
import { AgentKnowledgeTab } from '../components/RAG/Tabs/AgentKnowledgeTab'
import { ChatPanelMaster } from './ChatPage/ChatPanelMaster'
import { useUser } from '../contexts/UserContext'
import { useChatLayout } from '../hooks/useChatLayout'
import { ErrorBoundary } from '../components/ErrorBoundary'

export function RAGPage() {
    const { handle } = useParams<{ handle: string }>()
    const [agent, setAgent] = useState<Agent | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isChatOpen, setIsChatOpen] = useState(false)
    const [isResizing, setIsResizing] = useState(false)
    const [isChatFullscreen, setIsChatFullscreen] = useState(false)
    const { session } = useUser()

    // Use chat layout hook for persistent width
    const { chatWidth, setChatWidth } = useChatLayout('rag_page', 'right', 450)

    // Load Agent Data
    useEffect(() => {
        const load = async () => {
            if (!handle) return
            setIsLoading(true)
            try {
                const cleanHandle = handle.startsWith('@') ? handle.slice(1) : handle
                const agentData = await agentService.getAgentByHandle(cleanHandle)
                setAgent(agentData)
            } catch (e) {
                console.error(e)
            } finally {
                setIsLoading(false)
            }
        }
        load()
    }, [handle])

    // Handle chat resize
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault()
        setIsResizing(true)
    }

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return

            const newWidth = window.innerWidth - e.clientX
            // Limit width between 300px and 800px
            if (newWidth >= 300 && newWidth <= 800) {
                setChatWidth(newWidth)
            }
        }

        const handleMouseUp = () => {
            setIsResizing(false)
        }

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isResizing, setChatWidth])

    if (isLoading || !agent) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                Carregando Knowledge Base...
            </div>
        )
    }

    return (
        <div className="flex flex-col h-screen overflow-hidden bg-black">
            {/* TopNavigation Header */}
            <TopNavigation />

            {/* Flex Row Container */}
            <div className="flex-1 flex relative min-h-0">
                {/* Agent Sidebar - Always visible */}
                <AgentSidebar
                    agent={agent}
                    onChatClick={() => setIsChatOpen(!isChatOpen)}
                />

                {/* Main Content Area - Agent Knowledge */}
                <div className="flex-1 overflow-hidden h-full">
                    <AgentKnowledgeTab
                        session={session}
                        agentId={agent.id}
                    />
                </div>

                {/* ChatPanelMaster - Single persistent instance */}
                <AnimatePresence>
                    {isChatOpen && agent && (
                        <motion.div
                            key="chat-panel"
                            {...({
                                initial: { opacity: 0 },
                                animate: { opacity: 1 },
                                exit: { opacity: 0 }
                            } as any)}
                            transition={{ duration: 0.2 }}
                            className={
                                isChatFullscreen
                                    ? 'absolute inset-0 bg-white dark:bg-black z-[90]'
                                    : 'border-l border-gray-200 dark:border-gray-800 shadow-xl bg-white dark:bg-black hidden lg:block overflow-hidden h-full flex flex-col'
                            }
                            style={!isChatFullscreen ? { width: chatWidth } : undefined}
                        >
                            {/* Resize Handle - Only in normal mode */}
                            {!isChatFullscreen && (
                                <div
                                    onMouseDown={handleMouseDown}
                                    className="absolute left-0 top-0 w-4 h-full cursor-ew-resize -translate-x-1.5 z-10 hover:bg-blue-500/10 transition-colors"
                                />
                            )}

                            <ErrorBoundary>
                                <ChatPanelMaster
                                    isOpen={true}
                                    onClose={() => setIsChatOpen(false)}
                                    onFullscreenChange={setIsChatFullscreen}
                                    isFullscreen={isChatFullscreen}
                                    agentId={agent.id}
                                    agentName={agent.name}
                                    agent={agent}
                                    isEmbedded={true}
                                    position="right"
                                />
                            </ErrorBoundary>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}
